import {
  CREATE_JOURNAL_URL,
  get_headers,
  GET_JOURNALS_URL,
  UPDATE_JOURNAL_URL,
} from "@/utils/constants/constants";
import alert_mixin from "@/utils/mixins/alert_mixin";
import formatting_mixins from "@/utils/mixins/formatting_mixins";
import axios from "axios";

export default {
  name: "ApplicationJournal",
  mixins: [alert_mixin, formatting_mixins],

  data: () => ({
    headers: get_headers(),

    // Journal form
    journal_description: "",
    journal_date: "",
    attachments: [],
    hours_worked: 0,
    minutes_worked: 0,
    is_saving_journal: false,

    // Context
    application_id: "",
    user: "",

    // Journal list
    journals: [],
    total_hours: "0:00",

    // Edit Journal modal
    show_update_modal: false,
    edit_journal_id: null,
    edit_description: "",
    edit_hours: 0,
    edit_minutes: 0,
    edit_journal_date: "",
    edit_attachments: [],
    edit_existing_docs: [],

    // Edit Document modal
    show_edit_doc_modal: false,
    selected_doc: {},
    new_file: null,
  }),

  mounted() {
    this.application_id = sessionStorage.getItem("application_id") || "";
    this.user = sessionStorage.getItem("user_id") || "";
    this.fetch_journals();
  },

  methods: {
    /* -------------------------------------
       CREATE JOURNAL
    -------------------------------------- */
    add_attachment() {
      this.attachments.push({ document_name: "", file: null });
    },
    remove_attachment(index) {
      this.attachments.splice(index, 1);
    },
    handle_file_upload(event, index) {
      const file = event.target.files[0];
      if (file && file.size <= 5 * 1024 * 1024) {
        this.attachments[parseInt(index, 10)].file = file;
      } else {
        this.show_error("Invalid file type or size exceeds 5MB.");
      }
    },
    get_decimal_hours() {
      const total_minutes = this.hours_worked * 60 + this.minutes_worked;
      return parseFloat((total_minutes / 60).toFixed(2));
    },
    create_journal() {
      if (!this.journal_description.trim()) {
        this.show_error("Journal description is required.");
        return;
      }

      this.is_saving_journal = true;
      const formData = new FormData();
      formData.append("application_id", this.application_id);
      formData.append("journal_description", this.journal_description);
      formData.append("hours_spent", this.get_decimal_hours());
      formData.append("journal_date", this.journal_date);
      formData.append("user_id", this.user);

      this.attachments.forEach((att) => {
        if (att.file) {
          formData.append("file_url", att.file);
          formData.append("document_name", att.document_name);
        }
      });

      axios
        .post(CREATE_JOURNAL_URL, formData, {
          headers: { ...this.headers, "Content-Type": "multipart/form-data" },
        })
        .then((response) => {
          this.show_success(response.data.message || "Journal created successfully!");
          this.reset_form();
          this.fetch_journals();
        })
        .catch((error) => {
          this.show_error(
            error.response?.data?.message || "Failed to create journal. Please try again."
          );
        })
        .finally(() => {
          this.is_saving_journal = false;
        });
    },

    reset_form() {
      this.journal_description = "";
      this.journal_date = "";
      this.hours_worked = 0;
      this.minutes_worked = 0;
      this.attachments = [];
    },

    /* -------------------------------------
       FETCH JOURNALS
    -------------------------------------- */
    fetch_journals() {
      axios
        .get(GET_JOURNALS_URL, {
          headers: this.headers,
          params: { application_id: this.application_id },
        })
        .then((response) => {
          if (response.data?.status === "success") {
            this.journals = response.data.data || [];
            this.calculate_total_hours();
          } else {
            this.show_error("Error retrieving journals.");
          }
        })
        .catch((error) => {
          this.show_error(
            error.response?.data?.message || "Error retrieving journals."
          );
        });
    },

    calculate_total_hours() {
      let total_minutes = 0;
      this.journals.forEach((j) => {
        if (j.hours_spent_display) {
          const [h, m] = j.hours_spent_display.split(":").map(Number);
          total_minutes += h * 60 + m;
        }
      });
      const hours = Math.floor(total_minutes / 60);
      const minutes = total_minutes % 60;
      this.total_hours = `${hours}:${minutes.toString().padStart(2, "0")}`;
    },

    mark_doc_as_updated(doc) {
      doc.is_updated = true;
    },

    handle_replace_file(event, doc) {
      const file = event.target.files[0];
      if (file) {
        doc.new_file = file;
        doc.is_updated = true;
        this.show_success(`${doc.document_name} marked for update.`);
      } else {
        this.show_error("No file selected.");
      }
    },

    add_update_attachment() {
      this.edit_attachments.push({ document_name: "", file: null });
    },

    handle_update_file_upload(event, index) {
      const file = event.target.files[0];
      if (file && file.size <= 5 * 1024 * 1024) {
        this.edit_attachments[parseInt(index, 10)].file = file;
      } else {
        this.show_error("Invalid file type or size exceeds 5MB.");
      }
    },


    /* UPDATE JOURNAL*/
    update_application_journals(journal) {
      Promise.resolve()
        .then(() => {
          this.edit_journal_id = journal.id;
          this.edit_description = journal.journal_description || "";
          this.edit_journal_date = journal.journal_date || "";

          if (journal.hours_spent_display) {
            const [h, m] = journal.hours_spent_display.split(":").map(Number);
            this.edit_hours = h;
            this.edit_minutes = m;
          }

          this.edit_existing_docs = journal.documents || [];
          this.edit_attachments = [];
          this.show_update_modal = true;
        })
        .catch(() => {
          this.show_error("Failed to load journal data for editing.");
        });
    },


    update_journal() {

      const formData = new FormData();
      formData.append("journal_id", this.edit_journal_id);
      formData.append("journal_description", this.edit_description);

      const total_minutes = this.edit_hours * 60 + this.edit_minutes;
      formData.append("hours_spent", (total_minutes / 60).toFixed(2));
      formData.append("journal_date", this.edit_journal_date);

      // Make sure each document name and file are added as lists
      this.edit_attachments.forEach((att) => {
        if (att.file) {
          formData.append("file_url", att.file);
          formData.append("document_name", att.document_name || att.file.name);
        }
      });

      // Include queued UPDATED documents (those you edited in the small modal)
      this.edit_existing_docs.forEach((doc) => {
        if (doc.is_updated) {
          if (doc.new_file) {
            formData.append("updated_documents", doc.new_file);
          }
          formData.append("updated_document_ids[]", doc.id);
          formData.append("updated_document_names[]", doc.document_name);

          if (doc.new_file) {
            formData.append("updated_documents[]", doc.new_file);
          }
        }
      });

      axios
        .post(UPDATE_JOURNAL_URL, formData, {
          headers: {
            ...this.headers,
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          if (response.data?.status === "success" || response.data?.includes("success")) {
            this.show_success("Journal updated successfully!");
            this.fetch_journals();
            this.show_update_modal = false;
          } else {
            this.show_error("Failed to update journal.");
          }
        })
        .catch((error) => {
          this.show_error(error.response?.data?.message || "Error updating journal.");
        });
    },


    /* DOCUMENT HANDLING */
    view_document(doc) {
      window.open(doc.signed_file_url, "_blank");
    },

    open_edit_modal(doc) {
      // Assign fields directly (ensures reactivity)
      this.selected_doc.id = doc.id;
      this.selected_doc.document_name = doc.document_name || "";
      this.selected_doc.signed_file_url = doc.signed_file_url || "";
      this.new_file = null;
      this.show_edit_doc_modal = true;
    },

    handle_new_file(event) {
      this.new_file = event.target.files[0];
    },

    update_document_file() {
      if (!this.new_file) {
        this.show_error("Please select a new file to upload.");
        return;
      }

      const formData = new FormData();
      formData.append("document_id", this.selected_doc.id);
      formData.append("file_url", this.new_file);

      axios
        .post(UPDATE_JOURNAL_URL, formData, {
          headers: { ...this.headers, "Content-Type": "multipart/form-data" },
        })
        .then((response) => {
          if (response.data?.status === "success") {
            this.show_success("Document updated successfully!");
            this.show_edit_doc_modal = false;
            this.fetch_journals();
          } else {
            this.show_error(response.data?.message || "Failed to update document.");
          }
        })
        .catch((error) => {
          this.show_error(error.response?.data?.message || "Error updating document.");
        });
    },

    queue_document_update() {
      if (!this.new_file && !this.selected_doc.document_name?.trim()) {
        this.show_error("Please select a new file or edit the document name.");
        return;
      }

      // Create sanitized object with direct property access
      const sanitized_doc = {
        id: this.selected_doc.id,
        document_name: this.selected_doc.document_name
      };

      // Additional validation
      if (typeof sanitized_doc.id !== 'string' && typeof sanitized_doc.id !== 'number') {
        this.show_error("Invalid document ID");
        return;
      }

      if (typeof sanitized_doc.document_name !== 'string') {
        this.show_error("Invalid document name");
        return;
      }

      const index = this.edit_existing_docs.find_index(
        (d) => d.id === sanitized_doc.id
      );

      if (index !== -1) {
        const doc = Reflect.get(this.edit_existing_docs, index);
        Reflect.set(doc, 'new_file', this.new_file);
        Reflect.set(doc, 'is_updated', true);
        Reflect.set(doc, 'document_name', sanitized_doc.document_name.trim());
      }

      this.show_edit_doc_modal = false;
      this.show_success(
        `${sanitized_doc.document_name} is ready and will be uploaded when you save the journal.`
      );
    }


  },
};
